// Custom Panel Header Factory for Atlas Viewer
// SPDX-License-Identifier: MIT

import React from 'react';
import styled from 'styled-components';
import { PanelHeaderFactory, Icons } from '@kepler.gl/components';
import { colors } from '../colors';
import { resolveAssetsPath } from '../utils/assets';
import { useSelector } from 'react-redux';
import { selectHasSecureDatasets } from '../selectors/exportControl';
import { getAppVersion } from '../utils/version';
import { env } from '../env.config';

const customLogoPath = resolveAssetsPath('integral-analytics-logo.png');

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  /* Override Kepler's default bottom margin on panel header top */
  .side-panel__panel-header__top {
    margin-bottom: 0;
    align-items: center;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0;
  flex-shrink: 0;
`;

const Logo = styled.img`
  height: 28px;
  width: 28px;
  margin-right: 12px;
  filter: brightness(0) invert(1);
  flex-shrink: 0;
`;

const SecondRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: ${props => props.theme.sidePanelHeaderBg || '#29323c'};
  border-top: 1px solid ${props => props.theme.borderColor || 'rgba(255, 255, 255, 0.1)'};
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.div`
  color: ${props => props.theme.titleTextColor || colors.grayLight};
  font-size: 14px;
  font-weight: 600;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  line-height: 1.2;
`;

const VersionText = styled.span`
  font-size: 11px;
  color: ${props => props.theme.subtextColor || colors.primaryLightBlue};
  font-weight: 400;
  line-height: 1.2;
  opacity: 0.8;
`;

// Custom panel header factory for Atlas Viewer
export function CustomPanelHeaderFactory(...deps: [unknown, unknown]) {
  const PanelHeader = (PanelHeaderFactory as any)(...deps);
  const defaultItems = PanelHeader.defaultProps?.actionItems || [];

  return function CustomHeader(props: any) {
    // Build custom action items - synchronous, no async loading
    const hasSecure = useSelector(selectHasSecureDatasets as any);
    const appVersion = getAppVersion(); // Get version dynamically

    // Use configurable docs URL
    // In development: Falls back to GitHub docs if VitePress not running
    // In production: Uses integrated /docs/ from deployed build
    const docsUrl =
      import.meta.env.VITE_DOCS_URL ||
      (import.meta.env.DEV
        ? 'https://github.com/integralanalytics/atlas-viewer/tree/main/docs/user'
        : '/docs/');

    const actionItems: any[] = [
      {
        id: 'docs',
        iconComponent: Icons.Docs2,
        href: docsUrl,
        blank: true,
        tooltip: import.meta.env.DEV ? 'User Guide (GitHub)' : 'User Guide',
        onClick: () => {},
      },
    ];

    // Add feedback/roadmap button if URL is configured
    const feedbackUrl = import.meta.env.VITE_APP_FEEDBACK_URL;
    if (feedbackUrl) {
      actionItems.push({
        id: 'feedback',
        iconComponent: Icons.Speed,
        tooltip: 'Roadmap Portal',
        blank: true,
        onClick: () => {
          window.open(feedbackUrl, '_blank', 'noopener,noreferrer');
        },
      });
    }

    // Base set: prefer runtime-provided actionItems from upstream (kepler) then fall back to factory defaults
    const baseItems: any[] = (
      props.actionItems && Array.isArray(props.actionItems) ? props.actionItems : defaultItems
    ).filter(Boolean);

    // Identify meaningful items in upstream set
    const storageItem = baseItems.find((item: any) => item && item.id === 'storage');
    const saveItem = baseItems.find((item: any) => item && item.id === 'save');
    // Export detection: kepler versions sometimes rename or add variants. Match any id containing 'export'.
    const exportItems = baseItems.filter(
      (i: any) => i && typeof i.id === 'string' && /export/i.test(i.id)
    );

    // Tag the storage (Add Data) action item for E2E selection
    if (storageItem) {
      storageItem['data-testid'] = storageItem['data-testid'] || 'action-storage';
    }

    // Carry forward any non-export, non-storage/save items (avoid duplicates later)
    const passthrough = baseItems.filter(
      (i: any) => i && !['storage', 'save'].includes(i.id) && !i.id.toLowerCase().includes('export')
    );
    passthrough.forEach(i => actionItems.push(i));

    // Note: SQL Query Panel button moved to map controls (SqlPanelControl component)

    // Report Bug action button (opens in-app issue form, controlled by VITE_ISSUE_SUBMISSION_ENABLED)
    if (env.VITE_ISSUE_SUBMISSION_ENABLED) {
      const BugIcon = (Icons as any).Bug || Icons.Warning || Icons.Messages;
      actionItems.push({
        id: 'report-bug',
        iconComponent: BugIcon,
        tooltip: 'Report a Bug',
        href: '#',
        blank: false,
        'data-testid': 'action-report-bug',
        onClick: () => {
          (window as any).toggleIssueFormPanel?.();
        },
      });
    }

    // Release Notes (formerly Changelog) action button
    actionItems.push({
      id: 'release-notes',
      iconComponent: Icons.Clock,
      tooltip: 'Release Notes',
      href: '#',
      blank: false,
      'data-testid': 'action-release-notes',
      onClick: () => {
        (window as any).toggleReleaseNotesPanel?.();
      },
    });

    if (storageItem) actionItems.push(storageItem);
    if (saveItem) actionItems.push({ ...saveItem, label: null, tooltip: 'Share' });

    // Append export items with conditional disabling
    exportItems.forEach((itm: any) => {
      if (!itm) return;
      if (hasSecure) {
        actionItems.push({
          ...itm,
          disabled: true,
          tooltip: 'Export restricted: secure Azure datasets loaded',
          className: (itm.className || '') + ' secure-export-disabled',
          'data-testid': `secure-disabled-${itm.id}`,
        });
      } else {
        actionItems.push(itm);
      }
    });

    // Insert secure indicator (lock) ahead of export-related items if secure datasets loaded
    if (hasSecure) {
      // Distinct padlock indicator; pure visual state (non-interactive)
      const LockIcon = (Icons as any).Lock || Icons.Cancel || Icons.WarningSign || Icons.Warning; // graceful fallback
      actionItems.push({
        id: 'secure-indicator',
        iconComponent: LockIcon,
        tooltip: 'Secure mode: exports & raw data table restricted',
        onClick: () => {},
        disabled: true,
        'data-testid': 'secure-indicator',
      });
    }

    // Filter out any undefined items
    const filteredActionItems = actionItems.filter(Boolean);

    // Create our custom header with logo and branding
    return (
      <HeaderContainer>
        <PanelHeader
          {...props}
          logoComponent={() => (
            <LogoContainer>
              <Logo src={customLogoPath} alt='Integral Analytics Logo' />
            </LogoContainer>
          )}
          appName=''
          version=''
          actionItems={filteredActionItems}
        />
        <SecondRow>
          <TextContainer>
            <Title>Atlas Viewer</Title>
            <VersionText>{appVersion}</VersionText>
          </TextContainer>
        </SecondRow>
      </HeaderContainer>
    );
  };
}

// Set dependencies to match PanelHeaderFactory
CustomPanelHeaderFactory.deps = PanelHeaderFactory.deps;

// Extend the Window type for panel toggles
declare global {
  interface Window {
    toggleReleaseNotesPanel?: () => void;
    toggleSqlPanel?: () => void;
    toggleIssueFormPanel?: () => void;
  }
}

// Export replacement function for injectComponents
export const replacePanelHeader = () => [PanelHeaderFactory, CustomPanelHeaderFactory];
